import type { SlideDraft } from './generate';

/**
 * A deck imported from a real .pptx.
 *
 * The Propose and Workshop steps present decks the content team authors in
 * PowerPoint, not decks this app invents. `scripts/import-deck.mjs` extracts
 * the words from those files into `src/config/decks/*.ts`; this is the shape it
 * writes and the small amount of logic needed to render it.
 */
export interface SourceSlide {
  id: string;
  title: string;
  bullets: readonly string[];
  /** Speaker notes from the source deck, used as the talk track. */
  notes: string;
}

export interface SourceDeck {
  /** The file this came from, so the SE knows which deck they are looking at. */
  name: string;
  slides: readonly SourceSlide[];
}

/** How many bullets a slide can show before it stops being readable. */
const MAX_BULLETS = 8;

/**
 * Turns an imported deck into slides the viewer and the editor understand.
 *
 * Long slides get trimmed for the preview: an authored deck often carries
 * source citations and layout fragments that add nothing on screen, and the SE
 * can still see everything in the notes.
 */
export function sourceDeckToDrafts(deck: SourceDeck, kicker: string): SlideDraft[] {
  return deck.slides.map((slide) => ({
    id: slide.id,
    kicker,
    title: slide.title,
    bullets: slide.bullets.slice(0, MAX_BULLETS),
  }));
}

export function notesFor(deck: SourceDeck, slideId: string): string {
  return deck.slides.find((s) => s.id === slideId)?.notes ?? '';
}

/** The deck's name without the file extension, for showing on screen. */
export function deckName(deck: SourceDeck): string {
  return deck.name.replace(/\.pptx?$/i, '');
}
